// pi-theme.ts — terminal appearance prefs → the pi chat view's CSS custom
// properties. DOM-free on purpose (smoke transpiles + tests it in isolation,
// same lane as pi-live.ts): the mapping from a TerminalColors palette to the
// `--pi-*` slot set lives HERE, and pi-view.ts only writes the result onto
// its root element.
//
// Mapping doctrine: TS hands over RAW scheme slots only; every derived
// surface (bubble fills, tool-row grounds, dim text) is a CSS color-mix WITH
// --pi-bg so contrast tracks whichever of the 8 presets is active — raw ANSI
// values are only safe as accents, never as surfaces.

import type { TerminalColors, TerminalPrefs } from './term-types.js';

/** The Claude-desktop chat prose stack (Tiempos when installed, then the
 * closest serifs every OS ships). Exported as the `chatFontFamily` DEFAULT —
 * types.ts imports it for defaultTerminalPrefs(), and the server default in
 * server/config.ts mirrors the string (the server never imports client code).
 * Defined HERE and not in types.ts because this module must stay value-import
 * free: smoke transpiles it standalone, so any runtime import would fail to
 * resolve from the temp module. */
export const CLAUDE_CHAT_FONT =
  "'Tiempos Text','Charter','Iowan Old Style',Georgia,'Times New Roman',serif";

/** `chatFontFamily` sentinel: render chat prose in the terminal font. */
export const CHAT_FONT_TERMINAL = 'terminal';

/** Font prefs the mapper needs — structural subset of TerminalPrefs so the
 * smoke lane can feed literals without building the full prefs object. */
export type PiFontPrefs = Pick<
  TerminalPrefs,
  'fontFamily' | 'fontSize' | 'fontWeight' | 'fontWeightBold' | 'chatFontFamily'
>;

/**
 * The `--pi-*` custom-property set for one view root. `fontDelta` mirrors the
 * PaneTerm convention (panes render 2px smaller, floored at 9). Keys are
 * stable — pi-view.ts clears `--pi-sel-fg` explicitly when a palette drops
 * selectionForeground, and the smoke CSS-coverage check treats this key set
 * as "defined".
 */
export function piCssVars(p: TerminalColors, t: PiFontPrefs, fontDelta = 0): Record<string, string> {
  const vars: Record<string, string> = {
    '--pi-bg': p.background,
    '--pi-fg': p.foreground,
    '--pi-cursor': p.cursor,
    '--pi-sel-bg': p.selectionBackground,
    // Accents ride the ANSI slots: blue = "you" (user bubble, send), cyan =
    // instruments (tool chips, links), red/yellow/green = the status triad.
    '--pi-accent': p.blue,
    '--pi-accent-2': p.cyan,
    '--pi-err': p.red,
    '--pi-warn': p.yellow,
    '--pi-ok': p.green,
    '--pi-font': t.fontFamily,
    // Chat prose (assistant answers) — Claude-desktop serif by default; the
    // 'terminal' sentinel folds it back onto the terminal font.
    '--pi-font-text':
      t.chatFontFamily === CHAT_FONT_TERMINAL ? t.fontFamily : t.chatFontFamily || CLAUDE_CHAT_FONT,
    '--pi-font-size': `${Math.max(9, t.fontSize + fontDelta)}px`,
    '--pi-weight': String(t.fontWeight),
    '--pi-weight-bold': String(t.fontWeightBold ?? 700),
  };
  if (p.selectionForeground) vars['--pi-sel-fg'] = p.selectionForeground;
  return vars;
}
